import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { salesforceApi, queryKeys } from '@client/api/integrations';
import { useDebounce } from '@client/hooks/useDebounce';
import { Contacts } from '../components/integrations/Contacts';
import { LinkClientModal } from '../components/integrations/LinkClientModal';
import type { IntegrationContact } from '@client/api/types';
import type { Salesforce } from '@client/api/types';

function normalizeSalesforceContact(contact: Salesforce.Contact): IntegrationContact {
  return {
    id: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email,
    type: 'Contact',
    waterlilyClientId: contact.waterlilyClientId,
  };
}

export function AdvisorIntegrationsSalesforceContacts() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [contactToLink, setContactToLink] = useState<IntegrationContact | null>(null);
  const queryClient = useQueryClient();

  const { data: statusData } = useQuery({
    queryKey: ['salesforce', 'status'],
    queryFn: () => salesforceApi.auth.status(),
  });
  const instanceUrl = statusData?.instanceUrl;

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.salesforce.list('contacts', {
      page,
      perPage: pageSize,
      query: debouncedSearch,
    }),
    queryFn: () =>
      salesforceApi.contacts.list({
        page,
        perPage: pageSize,
      }),
    placeholderData: keepPreviousData,
  });

  const linkMutation = useMutation({
    mutationFn: ({ contactId, clientId }: { contactId: string; clientId: string }) =>
      salesforceApi.contacts.link({ contactId, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesforce.lists('contacts') });
      setContactToLink(null);
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (contactId: string) => salesforceApi.contacts.unlink(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.salesforce.lists('contacts') });
    },
  });

  const contacts = (data?.contacts ?? []).map(normalizeSalesforceContact);
  const totalPages = Math.ceil((data?.totalSize ?? 0) / pageSize) || 1;

  return (
    <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <div className="my-7 flex flex-col items-center justify-between gap-8 md:flex-row md:gap-0">
        <div className="flex items-center gap-5">
          <h1 className="text-primary-dark text-4xl font-semibold">Salesforce Contacts</h1>
        </div>
      </div>
      <div className="bg-primary-100 my-6 h-px w-full" />

      <Contacts
        contacts={contacts}
        totalPages={totalPages}
        isFetching={isFetching}
        error={error}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        search={search}
        onSearchChange={setSearch}
        onRefetch={refetch}
        onLink={setContactToLink}
        onUnlink={contactId => unlinkMutation.mutate(contactId)}
        isUnlinking={unlinkMutation.isPending}
        crmName="Salesforce"
        getExternalUrl={
          instanceUrl ? id => `${instanceUrl}/lightning/r/Contact/${id}/view` : undefined
        }
      />

      {!!contactToLink && (
        <LinkClientModal
          open={!!contactToLink}
          onClose={() => setContactToLink(null)}
          contact={contactToLink}
          onLink={(contactId, clientId) => linkMutation.mutate({ contactId, clientId })}
          isLinking={linkMutation.isPending}
          linkError={linkMutation.error}
          onReset={() => linkMutation.reset()}
        />
      )}
    </main>
  );
}
